import { isEmpty } from 'lodash';
import { match } from 'ts-pattern';

import { OmitNonDist } from '@dotfile/shared/common';

import {
  ControlAutomaticCodeModel,
  ControlAutomaticLogicModel,
  ControlBaseModel,
  ControlCompletionAggregateEnum,
  ControlManualModel,
  ControlScopeEntityTypeEnum,
  ControlTypeEnum,
} from '../../../../shared/models';
import yup from '../../../../utils/yup-extended';
import {
  CONTROL_INSTRUCTION_MAX_LENGTH,
  CONTROL_LABEL_KEY_MAX_LENGTH,
} from '../constants';

type ControlSchemaOption = {
  /**
   * Async function to determine if a key is unique
   * @param key
   * @returns
   */
  isKeyUnique: (key: string) => Promise<boolean>;
};

type ControlBaseSchema = yup.SchemaOf<
  OmitNonDist<
    ControlBaseModel,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'workspace'
    | 'workspaceId'
    | 'archivedAt'
    | 'cases'
    | 'templateRules'
    | 'type'
  >
>;

export const controlBaseSchema = ({
  isKeyUnique,
}: ControlSchemaOption): ControlBaseSchema => {
  return yup
    .object({
      label: yup.string().max(CONTROL_LABEL_KEY_MAX_LENGTH).required(),
      key: yup
        .string()
        .max(CONTROL_LABEL_KEY_MAX_LENGTH)
        .matches(
          /^[a-z][a-z_0-9]*$/,
          'key must start with a lower case letter and only contain lower case letters, digit and underscore',
        )
        .isUnique(
          isKeyUnique,
          "Another control with the same key '${value}' already exists",
        )
        .required(),
      instruction: yup
        .string()
        .max(CONTROL_INSTRUCTION_MAX_LENGTH)
        .optionalString(),
    })
    .noUnknown()
    .defined();
};

type ControlManualSchema = yup.SchemaOf<
  Omit<
    ControlManualModel,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'workspace'
    | 'workspaceId'
    | 'archivedAt'
    | 'cases'
    | 'templateRules'
  >
>;

export const controlManualSchema = (
  options: ControlSchemaOption,
): ControlManualSchema => {
  return controlBaseSchema(options).concat(
    yup.object({
      type: yup.mixed().oneOf([ControlTypeEnum.manual]).required().strict(),
    }),
  );
};

type ControlAutomaticLogicSchema = yup.SchemaOf<
  Omit<
    ControlAutomaticLogicModel,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'workspace'
    | 'workspaceId'
    | 'archivedAt'
    | 'cases'
    | 'templateRules'
  >
>;

export const controlAutomaticLogicSchema = (
  options: ControlSchemaOption,
): ControlAutomaticLogicSchema => {
  return controlBaseSchema(options)
    .concat(
      yup.object({
        type: yup
          .mixed()
          .oneOf([ControlTypeEnum.automatic_logic])
          .required()
          .strict(),
        scope: yup
          .mixed()
          .oneOf(Object.values(ControlScopeEntityTypeEnum))
          .required(),
        filterJsonLogic: yup.object().when('scope', {
          is: ControlScopeEntityTypeEnum.case,
          then: (schema) => schema.nullable(),
          otherwise: (schema) =>
            schema
              .transform((value) => {
                if (isEmpty(value)) {
                  return null;
                }

                return value;
              })
              .nullable()
              .jsonLogic(),
        }),
        completionAggregate: yup
          .mixed()
          .oneOf([...Object.values(ControlCompletionAggregateEnum), null])
          .when('scope', {
            is: ControlScopeEntityTypeEnum.case,
            then: (schema) => schema.nullable().optional(),
            otherwise: (schema) =>
              schema
                .oneOf(Object.values(ControlCompletionAggregateEnum))
                .required(),
          }),
        completionJsonLogic: yup
          .object()
          .jsonLogic({
            emptyErrorMessage:
              'completionJsonLogic is a required field for automatic_logic control',
          })
          .required(
            'completionJsonLogic is a required field for automatic_logic control',
          )
          .typeError(
            'completionJsonLogic is a required field for automatic_logic control',
          ),
      }),
    )
    .test(
      'scope-case-no-filter-json-logic',
      'filterJsonLogic must be null when scope is case',
      (value) => {
        if (value.scope === ControlScopeEntityTypeEnum.case) {
          return (
            value.filterJsonLogic === null ||
            value.filterJsonLogic === undefined
          );
        }
        return true;
      },
    )
    .test(
      'scope-case-no-completion-aggregate',
      'completionAggregate must be null when scope is case',
      (value) => {
        if (value.scope === ControlScopeEntityTypeEnum.case) {
          return (
            value.completionAggregate === null ||
            value.completionAggregate === undefined
          );
        }
        return true;
      },
    )
    .test(
      'scope-entity-requires-completion-aggregate',
      'completionAggregate is required when scope is company or individual',
      (value) => {
        if (
          value.scope === ControlScopeEntityTypeEnum.company ||
          value.scope === ControlScopeEntityTypeEnum.individual
        ) {
          return (
            value.completionAggregate !== null &&
            value.completionAggregate !== undefined
          );
        }
        return true;
      },
    );
};

type ControlAutomaticCodeSchema = yup.SchemaOf<
  Omit<
    ControlAutomaticCodeModel,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'workspace'
    | 'workspaceId'
    | 'archivedAt'
    | 'cases'
    | 'templateRules'
  >
>;
export const controlAutomaticCodeSchema = (
  options: ControlSchemaOption,
): ControlAutomaticCodeSchema => {
  return controlBaseSchema(options).concat(
    yup.object({
      type: yup
        .mixed()
        .oneOf([ControlTypeEnum.automatic_code])
        .required()
        .strict(),
      code: yup
        .string()
        .required('code is a required field for automatic_code control')
        .typeError('code is a required field for automatic_code control'),
    }),
  );
};

// @NOTE Using a function to return the schema according to the control type
// Typing not matching when using yup when operators
export const controlSchema = (
  type: ControlTypeEnum,
  options: ControlSchemaOption,
):
  | ControlManualSchema
  | ControlAutomaticLogicSchema
  | ControlAutomaticCodeSchema =>
  match(type)
    .with(ControlTypeEnum.manual, () => controlManualSchema(options))
    .with(ControlTypeEnum.automatic_logic, () =>
      controlAutomaticLogicSchema(options),
    )
    .with(ControlTypeEnum.automatic_code, () =>
      controlAutomaticCodeSchema(options),
    )
    .otherwise(() =>
      // fallback to manual as default
      controlManualSchema(options),
    );
